// Library imports
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import store, { RootState } from "store";
import { memo } from "react";

// Local imports
import GroupUserListItem from "./GroupUserListItem";
import Checkbox from "../../../Common/Components/Checkbox";
import {
  addUsersInGroups,
  getSalesXGroupDetails,
  getSalesXGroupUsersAction,
  removeUsersFromGroups,
  resetSelectedGroup,
  resetSelectedGroupUsers,
  resetUsers,
} from "../actions";
import { getQuerySalesXGroupId } from "utils";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import DeleteSelected from "modules/Campaigns/Audience/People/DeleteSelected";
import SelectedBottom from "modules/Common/Components/SelectedBottom";
import { SALESX_GROUP_DETAIL } from "constants/types/groupTypes";
import { getQueryCampaignId, getQueryEmailId, getQueryExportPopup } from "modules/EmailBrowser/utils";
import { useHistory } from "react-router-dom";
import { DEFAULT_CAMPAIGN_IMAGE } from "constants/types";
import UsersLoader from "../Loaders/UsersLoader";
import EmptyTabInfo from "modules/Content/Shared/EmptyTabInfo";

interface GroupListProps {
  personalityFilters: string[];
  searchTerm: string;
  setSearchTerm: (val: string) => void;
  addUsersHandler: (val: any) => void;
  removeUsersHandler: (val: any) => void;
  selectAllFlag: any;
  setSelectAllFlag: any;
  selectAllExcept: any;
  setSelectAllExcept: any;
  checkedItems: any;
  setCheckedItems: any;
}

const GroupList: React.FC<GroupListProps> = (props) => {
  // useQuery
  const queryParamSalesxGroupId = getQuerySalesXGroupId();

  const {
    personalityFilters,
    searchTerm,
    setSearchTerm,
    addUsersHandler,
    removeUsersHandler,
    selectAllFlag,
    setSelectAllFlag,
    selectAllExcept,
    setSelectAllExcept,
    checkedItems,
    setCheckedItems,
  } = props;

  //Redux States
  const users = useSelector((state: RootState) => state.salesXGroups.usersData.users);
  const usersLoading = useSelector((state: RootState) => state.salesXGroups.usersData.loading);
  const selectedUser = useSelector((state: RootState) => state.salesXGroups.selectedGroup.users);
  const selectedGroupTotalCount = useSelector((state: RootState) => state.salesXGroups.selectedGroup.totalCount);
  const selectedGroupEditable = useSelector((state: RootState) => state.salesXGroups.selectedGroup.isDeletable);
  const totalUsers = useSelector((state: RootState) => state.salesXGroups.usersData.totalCount);

  const selectedUsersLoading = useSelector((state: RootState) => state.salesXGroups.selectedGroup.loading);
  const globalListing = useSelector((state: RootState) => state.salesXGroups.selectedGroup.showGlobalListing);
  const deleteLoading = useSelector((state: RootState) => state.salesXGroups.selectedGroup.deleteLoading);
  const saveLoading = useSelector((state: RootState) => state.salesXGroups.selectedGroup.saveLoading);

  // Functions
  const getPagination = (reset?: boolean) => {
    let object: any = {};
    object.limit = 10;
    object.offset = reset ? 0 : globalListing ? users.length : selectedUser.length;
    object.groupId = queryParamSalesxGroupId;
    if (personalityFilters.length > 0) {
      object.personalityType = personalityFilters.join(",");
    }
    if (searchTerm.trim() !== "") object.searchTerm = searchTerm;
    return object;
  };

  const handleNext = () => {
    if (globalListing) {
      getSalesXGroupUsersAction(getPagination());
    } else {
      getSalesXGroupDetails(getPagination());
    }
  };

  useEffect(() => {
    // if (queryParamSalesxGroupId && !selectedUsersLoading) getSalesXGroupDetails(getPagination());
    if (globalListing) {
      getSalesXGroupUsersAction(getPagination());
    }
  }, []);

  const onClickChecked = (id: any) => {
    if (selectAllFlag) {
      setSelectAllFlag(false);
      setSelectAllExcept([id]);
    } else if (selectAllExcept.length > 0) {
      if (selectAllExcept.includes(id)) {
        if (selectAllExcept.length - 1 === 0) {
          setSelectAllFlag(true);
        }
        setSelectAllExcept(
          selectAllExcept.filter((selected: any) => {
            return selected !== id;
          })
        );
      } else {
        setSelectAllExcept([...selectAllExcept, id]);
      }
    } else {
      if (checkedItems.includes(id)) {
        setCheckedItems(
          checkedItems.filter((item: any) => {
            return item !== id;
          })
        );
      } else {
        setCheckedItems([...checkedItems, id]);
        if (globalListing) {
          if (checkedItems.length + 1 === totalUsers) {
            setSelectAllFlag(true);
          }
        } else if (checkedItems.length + 1 === selectedGroupTotalCount) {
          setSelectAllFlag(true);
        }
      }
    }
  };

  const clearSelectionHandler = () => {
    setCheckedItems([]);
    setSelectAllFlag(false);
  };

  const deleteAllToggle = () => {
    if (!selectAllFlag) {
      setSelectAllExcept([]);
    }
    setCheckedItems([]);
    setSelectAllFlag(!selectAllFlag);
  };

  useEffect(() => {
    if (globalListing && !usersLoading) {
      resetUsers();
      getSalesXGroupUsersAction(getPagination(true));
    } else if (!selectedUsersLoading && !usersLoading) {
      resetSelectedGroupUsers();
      getSalesXGroupDetails(getPagination(true));
    }
  }, [personalityFilters]);

  const total = globalListing ? totalUsers : selectedGroupTotalCount;

  const disableSelectAll = globalListing ? (usersLoading || users.length === 0) : (selectedUsersLoading || selectedUser.length === 0);

  return (
    //* commenting the following condition as we need to show messages in case of no data.
    // (globalListing ? users : selectedUser).length > 0 && (
    <Wrapper globalListing={globalListing}>
      <div id="scrollableDiv" className="scrollable-table">
        <InfiniteLoader
          dataLength={globalListing ? users.length : selectedUser.length}
          next={handleNext}
          hasMore={true}
          loader={<UsersLoader loading={usersLoading || selectedUsersLoading} count={5} />}
        >
          <table>
            <thead>
              <tr>
                <td className="checkbox">
                  <Checkbox disabled={disableSelectAll} width={"18px"} height={"18px"} checked={selectAllFlag} onChange={deleteAllToggle} />
                </td>
                <td className="h-name">
                  <span className="sortable-td">
                    <span className="left-align field name-field">Name</span>
                  </span>
                </td>
                <td className="personality-col">
                  <span className="sortable-td">
                    <span className="left-align field personality-text">Company</span>
                  </span>
                </td>
                <td className="personality-col">
                  <span className="sortable-td">
                    <span className="left-align field personality-text">Personality</span>
                  </span>
                </td>
              </tr>
            </thead>
            <tbody>
              {globalListing
                ? users.map((user) => (
                  <GroupUserListItem
                    key={user.userId}
                    userColor={user.personalityColor}
                    userImage={user.profilePic ?? DEFAULT_CAMPAIGN_IMAGE}
                    // userName={`${user.firstName ?? user.email} ${user.lastName ?? ""}`}
                    userName={`${user.firstName} ${user.lastName}`.trim()}
                    userEmail={user.email}
                    userDesignation={user.designation}
                    userPersonality={user.personalityType}
                    isChecked={
                      selectAllFlag
                        ? true
                        : selectAllExcept.length > 0
                          ? !selectAllExcept.includes(user.userId)
                          : checkedItems.includes(user.userId)
                    }
                    onClickChecked={() => onClickChecked(user.userId)}
                    company={user.company}
                  />
                ))
                : selectedUser.map((user) => (
                  <GroupUserListItem
                    key={user.userId}
                    userColor={user.personalityColor}
                    userImage={user.profilePic ?? DEFAULT_CAMPAIGN_IMAGE}
                    // userName={`${user.firstName ?? user.email} ${user.lastName ?? ""}`}
                    userName={`${user.firstName} ${user.lastName}`.trim()}
                    userEmail={user.email}
                    userDesignation={user.designation}
                    userPersonality={user.personalityType}
                    isChecked={
                      selectAllFlag
                        ? true
                        : selectAllExcept.length > 0
                          ? !selectAllExcept.includes(user.userId)
                          : checkedItems.includes(user.userId)
                    }
                    onClickChecked={() => onClickChecked(user.userId)}
                    company={user.company}
                  />
                ))}
            </tbody>
          </table>
          {!globalListing && selectedUser.length === 0 && !selectedUsersLoading ? (
            <EmptyTabInfo description={`No users found ${searchTerm !== "" ? "against your search" : ""}`} />
          ) : globalListing && users.length === 0 && !usersLoading && searchTerm !== "" ? (
            <EmptyTabInfo description={`No users found against your search`} />
          ) : globalListing && users.length === 0 && !usersLoading ? (
            <EmptyTabInfo description={`No users found`} />
          ) : null}
        </InfiniteLoader>
        {/** showing loader when user lands first time */}
        {selectedUser.length === 0 && selectedUsersLoading ? (
          <UsersLoader loading={usersLoading || selectedUsersLoading} count={5} />
        ) : users.length === 0 && usersLoading ? (
          <UsersLoader loading={usersLoading || selectedUsersLoading} count={5} />
        ) : null}
        {globalListing ? (
          <SelectedBottom
            onClearSelection={clearSelectionHandler}
            selectedCount={
              selectAllFlag
                ? total
                : selectAllExcept.length > 0
                  ? total - selectAllExcept.length
                  : checkedItems.length
            }
            selectableWorkflowsCount={total}
            actionText="Add"
            confirmTitle={`${selectAllFlag
              ? total
              : selectAllExcept.length > 0
                ? total - selectAllExcept.length
                : checkedItems.length
              } selected person(s) will be added.`}
            askConfirmDelete={true}
            actionHandler={addUsersHandler}
            loading={saveLoading}
            selectAllFlag={selectAllFlag}
            setSelectAllFlag={setSelectAllFlag}
            excludedWorkflowsIds={selectAllExcept}
          />
        ) : (
          <SelectedBottom
            onClearSelection={clearSelectionHandler}
            selectedCount={
              selectAllFlag
                ? total
                : selectAllExcept.length > 0
                  ? total - selectAllExcept.length
                  : checkedItems.length
            }
            selectableWorkflowsCount={total}
            actionText="Delete Selected"
            confirmTitle={`${selectAllFlag
              ? total
              : selectAllExcept.length > 0
                ? total - selectAllExcept.length
                : checkedItems.length
              } selected person(s) will be removed.`}
            askConfirmDelete={true}
            actionHandler={removeUsersHandler}
            // loading={!selectedGroupEditable}
            loading={deleteLoading || !selectedGroupEditable}
            selectAllFlag={selectAllFlag}
            setSelectAllFlag={setSelectAllFlag}
            excludedWorkflowsIds={selectAllExcept}
          />
        )}
      </div>
    </Wrapper>
    // )
  );
};

export default memo(GroupList);

type WrapperProps = {
  globalListing?: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  margin-bottom: 25px;
  .field {
    font-family: "Roboto", sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 18px;
    letter-spacing: -0.33px;
    color: #5e7079;
    &.name-field {
      margin-left: 68px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        margin-left: 54px;
      }
    }
  }
  .checked {
    border: 1px solid blue;
    border-radius: 10px;
  }
  .scrollable-table {
    margin-bottom: 40px; // Prevent overlap of footer
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-bottom: 20px;
    }
    position: relative;
    box-sizing: border-box;
    > div {
      overflow: hidden;
    }
    table {
      width: 100%;
      border: none;
      margin-bottom: 20px;
      position: relative;
      border-bottom: 1px solid #dddddd;
      &:before {
        content: "";
        position: absolute;
        width: 100%;
        height: calc(100% + 3px);
        transition-duration: 0.2s;
        box-sizing: border-box;
        margin-top: -2px;
        height: 1px;
        @media all and (min-width: 1440px) {
          margin-top: -3px;
        }
      }
      tr {
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        &:before {
          content: "";
          position: absolute;
          width: 100%;
          height: calc(100% + 3px);
          transition-duration: 0.2s;
          box-sizing: border-box;
          margin-top: -2px;
          border-bottom: 1px solid #dddddd;
          @media all and (min-width: 1440px) {
            margin-top: -3px;
          }
        }
        &.active-new {
          background: #cdf1d9;
          border-radius: 8px;
          border-bottom: none;
          &:before {
            border: 3px solid #27ce0a;
            border-radius: 8px;
            height: calc(100% + 2px);
          }
          td {
            overflow: hidden;
            &:first-child {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }
            &:last-child {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
            }
          }
        }
        &.active {
          background: #fff;
          border-radius: 8px;
          td {
            overflow: hidden;
            &:first-child {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }
            &:last-child {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
            }
          }
        }
        &.drag-active {
          position: relative;
          border-radius: 8px;
          td {
            overflow: hidden;
            &:first-child {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }
            &:last-child {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
            }
          }
          &:after {
            content: "";
            height: 100%;
            width: calc(100% + 10px);
            position: absolute;
            top: 0;
            left: 0;
            z-index: 99999;
            background-color: #f2efeb;
            border-radius: 8px;
            margin: 0 -5px;
            display: block;
          }
        }
        td {
          padding: 9px 10px;
          &.checkbox {
            width: 10px;
            padding-right: 0 !important;
          }
          &:nth-child(2) {
            width: 345px;
          }
          &:nth-child(3) {
            width: 235px;
          }
          &.company-name {
            color: #424b50;
            font-weight: 500;
            font-size: 18px;
            letter-spacing: -0.375px;
            font-family: "Roboto", sans-serif;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 14px;
            }
          }
          .profile-img-wrapper {
            padding: 2px;
            width: 54px;
            height: 54px;
            border-radius: 100%;
            float: left;
            overflow: hidden;
            margin-top: 9px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              width: 41px;
              height: 41px;
              margin-top: 6px;
            }
            .profile-img {
              border-radius: 100%;
              width: 100%;
              height: 100%;
            }
          }
          .profile {
            float: left;
            padding-left: 14px;
            text-align: left;
            max-width: calc(100% - 55px);
            .hidden-contact-name {
              height: 1px;
              overflow: hidden;
              display: block;
              opacity: 0;
              font-size: 20px;
              font-family: "Roboto", sans-serif;
              font-weight: 500;
            }
            .name {
              color: #000000;
              font-size: 20px;
              font-family: "Roboto", sans-serif;
              font-weight: 500;
              margin-bottom:0px;
              position: relative;
              z-index: 1;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 14px;
              }
            }
            .email {
              color: #27ce0a;
              font-size: 14px;
              font-family: "Roboto", sans-serif;
              font-weight: 500;
              margin-bottom: 4px;
              line-height: 12px;
              position: relative;
              z-index: 1;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
                line-height: 8px;
              }
            }
            .designation {
              font-family: "Roboto";
              font-style: normal;
              font-weight: 500;
              font-size: 14px;
              line-height: 16px;
              color: #424b50;
              margin-bottom:0px;
              position: relative;
              z-index: 1;
              margin-bottom:5px;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
                line-height: 14px;
              }
            }
          }
          .personality {
            color: #424b50;
            font-size: 18px;
            font-family: "robotomedium";
            font-weight: 500;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 13px;
            }
          }
        }
      }
    }
  }
`;
